import * as queryString from './queryString';
import { authFetch } from './fetch';
import routes from '../constants/routes';
import routing from './routing';
import { getAuthClientSession } from './AuthClientSession';
import { AUTH_SERVICE } from '../constants/auth';

export type TIdentityProvider = 'Google' | 'Facebook' | 'Highlander';
export interface IRecatcha {
    recaptcha?: boolean;
    token?: string;
}

export interface IOAuthAuthorize {
    clientId: string;
    identityProvider?: TIdentityProvider;
    responseType: string;
    redirectUri: string;
}

export interface IOAuthParams {
    client_id: string;
    code?: string;
    state?: string;
    response_type: string;
    redirect_uri: string;
    redirectUri?: string;
    LOGIN_V2?: string;
}

export interface IOAuthSocialToken extends IOAuthAuthorize {
    originalRedirectUri: string;
}

const authClientSession = getAuthClientSession({
    authServer: AUTH_SERVICE as string,
});

const associateSoftwareToken = () => {
    return authFetch('/associateSoftwareToken', {
        method: 'POST',
    });
};

/**
 * @description authorize endpoint that will allow applications to be logged in
 * @param qsParams IOAuthAuthorize
 */
const authorize = (qsParams: any) => {
    const qs = queryString.stringify(qsParams as queryString.IParams);
    return authFetch(`${routes.AUTHORIZE}?${qs}`);
};

export const authorizeV2 = (params: { redirectUri: string; identityProvider: string }) => {
    return authFetch(`/oauth/v2/authorize/${params.identityProvider}?redirect_uri=${params.redirectUri}`);
};

/**
 * Change Password
 */
export interface IChangePassword {
    oldPassword: string;
    newPassword: string;
}
/**
 * change password
 */
const changePassword = (payload: IChangePassword) => {
    return authFetch(routes.CHANGE_PASSWORD, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

export interface IChangeProfile {
    firstname?: string;
    lastname?: string;
    email?: string;
    dob?: string;
    mobile?: string;
}

/**
 * Change profile details
 */
const changeProfile = (payload: IChangeProfile) => {
    return authFetch(`${routes.PROFILE}${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'PUT',
    });
};

/**
 * Change profile details
 */
const changeEmail = (payload: { email: string }) => {
    return authFetch(`${routes.PROFILE}/email${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'PUT',
    });
};

/**
 * Confirm email change
 */
const confirmChangeEmail = (payload: { code: string; username: string; newEmail: string }) => {
    return authFetch(`${routes.PROFILE}/confirmChangeEmail${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'PUT',
    });
};

/**
 * Verify New Email
 */
const verifyNewEmail = (payload: { code: string; username: string; newEmail: string }) => {
    return authFetch(`${routes.PROFILE}/verifyNewEmail${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'PUT',
    });
};

/**
 * Change profile details
 */
export const requestEmailChange = (payload: { newEmail: string }) => {
    return authFetch(`${routes.PROFILE}/requestEmailChange${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'PUT',
    });
};

export interface IConformForgotPassword {
    username: string;
    code: string;
    newPassword: string;
}

const confirmForgotPassword = (payload: IConformForgotPassword) => {
    return authFetch(routes.CONFIRM_FORGOT_PASSWORD, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

export interface IConfirmSignUp {
    username: string;
    code: string;
}
export const DEFAULT_SIGNUP_VERSION = '2';
export const confirmSignUp = (payload: IConfirmSignUp, version: string = DEFAULT_SIGNUP_VERSION) => {
    return authFetch(`/v${version}/signUp/confirmSignUp`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

interface IForgotPassword extends IRecatcha {
    username: string;
}
/**
 *
 * @param username
 */
const forgotPassword = (payload: IForgotPassword) => {
    return authFetch(routes.FORGOT_PASSWORD, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

/**
 * Get Profile details
 */
const getProfile = () => {
    return authFetch(`${routes.PROFILE}${routing.location.search}`);
};

/**
 * Get Profile details
 */
export const getUserInfo = () => {
    return authClientSession.getUserInfo();
};

export interface IResetPassword {
    password: string;
    confirmPassword: string;
}
/**
 *
 * @param password
 * @param confirmPassword
 */
const resetPassword = (payload: IResetPassword) => {
    return authFetch(`${routes.RESET_PASSWORD}${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

export interface IRespondToMfa {
    mfaCode: string;
    challengeName?: string;
}
/**
 *
 * @param mfaCode
 */
const respondToMfa = (payload: IRespondToMfa) => {
    return authFetch(`/respondToMfa${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

export interface ISignIn extends IRecatcha {
    username: string;
    password: string;
}
/**
 * sign in endpoint
 */
const signIn = (payload: ISignIn) => {
    return authFetch(`${routes.SIGN_IN}${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

/**
 * sign in endpoint
 */
export const signInV2 = (payload: ISignIn) => {
    return authFetch(`/clientSession/login${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

/**
 * sign out endpoint
 */
const signOut = () => {
    return authFetch(`${routes.SIGN_OUT}${routing.location.search}`);
};

export interface INewUser extends IRecatcha {
    firstname: string;
    lastname: string;
    username: string;
    password: string;
}

/**
 *
 * @param body
 */
const signUp = (payload: INewUser) => {
    return authFetch(`/v2${routes.SIGN_UP}${routing.location.search}`, {
        body: JSON.stringify(payload),
        // ...headers,
        method: 'POST',
    });
};

const signUpV3 = (payload: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    companyName: string;
    companyPhone: string;
    companyWebsite?: string;
    countryCode: string;
    isOperator: boolean;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    recaptcha?: boolean;
    token?: string;
}) => {
    return authFetch(`/v3${routes.SIGN_UP}${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

const hubspotSignUp = (payload: {
    firstName: string;
    lastName: string;
    email: string;
    companyName: string;
    companyPhone: string;
    companyWebsite: string;
    companyType: string;
    country: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    recaptcha?: boolean;
    token?: string;
}) => {
    return authFetch(`/hubspot${routes.SIGN_UP}${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

const hubspotSignUpValidation = (payload: {
    firstName: string;
    lastName: string;
    email: string;
    companyName: string;
    companyPhone: string;
    companyWebsite: string;
    companyType: string;
    country: string;
}) => {
    return authFetch(`/hubspot${routes.SIGN_UP}/validate${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

const hubspotSignUpEnabled = () => authFetch(`/hubspot${routes.SIGN_UP}/enabled`);

/**
 * @description social login
 * @param qsParams IOAuthParams
 */
const socialToken = (qsParams: any) => {
    // const data = needs proper encoding
    const body = queryString.stringify(qsParams as queryString.IParams);
    return authFetch(`${routes.IDP_TOKEN}`, {
        body,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        method: 'POST',
    });
};

/**
 * @description social login
 * @param qsParams IOAuthParams
 */
export const socialTokenV2 = (params: { code: string; queryParams?: queryString.IParams; idpRedirectUrl: string }) => {
    const queryParams = queryString.stringify(params.queryParams as queryString.IParams);
    return authFetch(`/oauth/v2/idptoken?${queryParams}`, {
        body: JSON.stringify({
            code: params.code,
            redirectUri: params.idpRedirectUrl,
        }),
        method: 'POST',
    });
};

export interface IVerifyMfaCode {
    mfaCode: string;
}
/**
 *
 * @param mfaCode
 */
const verifyMfaCode = (payload: IVerifyMfaCode) => {
    return authFetch('/verifyMfaCode', {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

export const resendVerificationCode = (payload: { username: string }, version: string = DEFAULT_SIGNUP_VERSION) => {
    return authFetch(`/v${version}/signUp/resendConfirmationCode${routing.location.search}`, {
        body: JSON.stringify(payload),
        method: 'POST',
    });
};

export default {
    associateSoftwareToken,
    authorize,
    authorizeV2,
    changeEmail,
    confirmChangeEmail,
    requestEmailChange,
    verifyNewEmail,
    changePassword,
    changeProfile,
    confirmForgotPassword,
    confirmSignUp,
    forgotPassword,
    getUserInfo,
    getProfile,
    resetPassword,
    respondToMfa,
    signIn,
    signOut,
    signUp,
    signUpV3,
    hubspotSignUp,
    hubspotSignUpValidation,
    hubspotSignUpEnabled,
    socialToken,
    socialTokenV2,
    verifyMfaCode,
    resendVerificationCode,
};
